import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Collapse,
  Popover,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Group as GroupIcon,
  Build as BuildIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Code as CodeIcon,
  LocalShipping as LocalShippingIcon,
  Schedule as ScheduleIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  Language as LanguageIcon,
  AccountBalance as AccountBalanceIcon,
  Analytics as AnalyticsIcon,
  ChevronRight as ChevronRightIcon,
  Factory as FactoryIcon,
  PeopleAlt as PeopleAltIcon,
  Visibility as VisibilityIcon,
  ExpandLess,
  ExpandMore,
  Lock as LockIcon,
  MoreVert as MoreVertIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import ChangePasswordDialog from './users/ChangePasswordDialog';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Language } from '../i18n';

const DRAWER_WIDTH = 240;

interface SubMenuItem {
  titleKey: keyof typeof import('../i18n/translations/en').en.nav;
  path: string;
  icon: React.ReactNode;
}

interface MenuItem {
  titleKey: keyof typeof import('../i18n/translations/en').en.nav;
  path: string;
  icon: React.ReactNode;
  subItems?: SubMenuItem[];
}

const menuItemsConfig: MenuItem[] = [
  { titleKey: 'home', path: '/', icon: <HomeIcon /> },
  { titleKey: 'companies', path: '/companies', icon: <BusinessIcon /> },
  { titleKey: 'employees', path: '/employees', icon: <GroupIcon /> },
  { titleKey: 'machines', path: '/machines', icon: <BuildIcon /> },
  { titleKey: 'receivedNotes', path: '/received-notes', icon: <ReceiptIcon /> },
  { titleKey: 'programs', path: '/programs', icon: <CodeIcon /> },
  { titleKey: 'deliveries', path: '/deliveries', icon: <LocalShippingIcon /> },
  { titleKey: 'invoices', path: '/invoices', icon: <DescriptionIcon /> },
  { titleKey: 'inventory', path: '/inventory', icon: <InventoryIcon /> },
  { titleKey: 'employeeShifts', path: '/employee-shifts', icon: <ScheduleIcon /> },
  { 
    titleKey: 'metrics', 
    path: '/metrics', 
    icon: <AnalyticsIcon />,
    subItems: [
      { titleKey: 'productionMetrics', path: '/metrics/production', icon: <FactoryIcon /> },
      { titleKey: 'employeeMetrics', path: '/metrics/employee', icon: <PeopleAltIcon /> },
      { titleKey: 'viewAnalytics', path: '/metrics/view-analytics', icon: <VisibilityIcon /> },
    ]
  },
  { titleKey: 'finance', path: '/finance', icon: <AccountBalanceIcon /> },
  { titleKey: 'users', path: '/users', icon: <PersonIcon /> },
  { titleKey: 'auditLog', path: '/audit-log', icon: <HistoryIcon /> },
  { titleKey: 'settings', path: '/settings', icon: <SettingsIcon /> },
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState<{ id: number; email: string; roles?: string[] } | null>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<string | null>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, translations: t } = useTranslation();

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleChangePasswordClick = () => {
    handleUserMenuClose();
    setChangePasswordOpen(true);
  };

  const handleLogoutClick = () => {
    handleUserMenuClose();
    handleLogout();
  };

  const handleSubMenuOpen = (event: React.MouseEvent<HTMLElement>, titleKey: string) => {
    setSubMenuAnchorEl(event.currentTarget);
    setActiveSubMenu(titleKey);
  };

  const handleSubMenuClose = () => {
    setSubMenuAnchorEl(null);
    setActiveSubMenu(null);
  };

  const handleMobileSubmenuToggle = (titleKey: string) => {
    setExpandedMobileMenu(expandedMobileMenu === titleKey ? null : titleKey);
  };

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    handleLangMenuClose();
  };

  useEffect(() => {
    // Load user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/users/sign_out`, {
        method: 'DELETE',
        headers: {
          'Authorization': token || '',
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      navigate('/login');
    }
  };

  const getUserInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  const isAdmin = user?.roles?.includes('admin') || false;
  const isCompanyRep = user?.roles?.includes('company_rep') || false;

  // Check if user is on login page
  const isLoginPage = location.pathname === '/login';

  // Paths that company_rep users can access
  const companyRepAllowedPaths = ['/', '/programs', '/received-notes', '/deliveries', '/invoices'];

  // Filter menu items based on user role and add translated titles
  const filteredMenuItems = menuItemsConfig
    .filter(item => {
      // If user is company_rep, only show allowed paths
      if (isCompanyRep && !isAdmin) {
        return companyRepAllowedPaths.includes(item.path);
      }
      // Show Users, Audit Log, and Settings menu only to admins
      if (item.path === '/users' || item.path === '/audit-log' || item.path === '/settings') {
        return isAdmin;
      }
      return true;
    })
    .map(item => ({
      ...item,
      title: t.nav[item.titleKey],
      subItems: item.subItems?.map(subItem => ({
        ...subItem,
        title: t.nav[subItem.titleKey],
      })),
    }));

  // Check if any submenu path is active
  const isSubMenuPathActive = (item: typeof filteredMenuItems[0]) => {
    if (!item.subItems) return false;
    return item.subItems.some(subItem => location.pathname === subItem.path);
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box>
        <Toolbar sx={{ gap: 1.5, py: 2, justifyContent: 'flex-start' }}>
          <Box
            component="img"
            src="/apple-touch-icon.png"
            alt={t.appName}
            sx={{
              width: 40,
              height: 40,
              objectFit: 'contain',
            }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
            {t.appName}
          </Typography>
        </Toolbar>
        <Divider />
        <List>
          {filteredMenuItems.map((item) => (
            <React.Fragment key={item.path}>
              {item.subItems ? (
                // Menu item with submenu
                <>
                  {/* Desktop: Show popover on hover */}
                  {!isMobile && (
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={isSubMenuPathActive(item)}
                        onMouseEnter={(e) => handleSubMenuOpen(e, item.titleKey)}
                        onClick={() => {
                          // Navigate to first submenu item on click
                          if (item.subItems && item.subItems.length > 0) {
                            handleNavigation(item.subItems[0].path);
                          }
                        }}
                        sx={{ px: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 56 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.title} />
                        <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {/* Mobile: Show collapsible submenu */}
                  {isMobile && (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={isSubMenuPathActive(item)}
                          onClick={() => handleMobileSubmenuToggle(item.titleKey)}
                          sx={{ px: 2 }}
                        >
                          <ListItemIcon sx={{ minWidth: 56 }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.title} />
                          {expandedMobileMenu === item.titleKey ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={expandedMobileMenu === item.titleKey} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {item.subItems.map((subItem) => (
                            <ListItem key={subItem.path} disablePadding>
                              <ListItemButton
                                selected={location.pathname === subItem.path}
                                onClick={() => handleNavigation(subItem.path)}
                                sx={{ pl: 4, py: 1 }}
                              >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                  {subItem.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={subItem.title} 
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </>
                  )}
                </>
              ) : (
                // Regular menu item without submenu
                <ListItem disablePadding>
                  <ListItemButton
                    selected={
                      item.path === '/' 
                        ? location.pathname === '/' 
                        : location.pathname.startsWith(item.path)
                    }
                    onClick={() => handleNavigation(item.path)}
                    onMouseEnter={handleSubMenuClose}
                    sx={{ px: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 56 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.title} />
                  </ListItemButton>
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
      
      {/* User Profile Section at Bottom */}
      {user && (
        <Box sx={{ mt: 'auto' }}>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar 
                sx={{ 
                  bgcolor: theme.palette.primary.main, 
                  width: 40, 
                  height: 40,
                  mr: 1.5 
                }}
              >
                {getUserInitials(user.email)}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  fontWeight="600"
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {user.email.split('@')[0]}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block'
                  }}
                >
                  {user.email}
                </Typography>
              </Box>
              <Tooltip title={t.actions}>
                <IconButton
                  size="small"
                  onClick={handleUserMenuOpen}
                  sx={{ ml: 0.5 }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {!isLoginPage && (
        <>
          <AppBar
            position="fixed"
            className="no-print"
            sx={{
              width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
              ml: { sm: `${DRAWER_WIDTH}px` },
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Typography 
                variant="h6" 
                noWrap 
                component="div" 
                sx={{ 
                  flexGrow: 1, 
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                }}
              >
                {location.pathname === '/' 
                  ? t.nav.home 
                  : filteredMenuItems.find(item => item.path !== '/' && location.pathname.startsWith(item.path))?.title || t.nav.home}
              </Typography>
              
              {/* Language Selector */}
              <Tooltip title={t.language.select}>
                <IconButton
                  color="inherit"
                  onClick={handleLangMenuOpen}
                  sx={{ ml: 1 }}
                >
                  <LanguageIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem 
                  onClick={() => handleLanguageChange('en')}
                  selected={language === 'en'}
                >
                  {t.language.english}
                </MenuItem>
                <MenuItem 
                  onClick={() => handleLanguageChange('ta')}
                  selected={language === 'ta'}
                >
                  {t.language.tamil}
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>

          <Box
            component="nav"
            className="no-print"
            sx={{ 
              width: { sm: DRAWER_WIDTH }, 
              flexShrink: { sm: 0 },
            }}
          >
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better mobile performance
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: DRAWER_WIDTH,
                },
              }}
            >
              {drawer}
            </Drawer>
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: DRAWER_WIDTH,
                  overflowX: 'hidden',
                },
              }}
              open
            >
              {drawer}
            </Drawer>
          </Box>

          {/* Submenu Popover for Desktop */}
          <Popover
            open={Boolean(subMenuAnchorEl) && Boolean(activeSubMenu)}
            anchorEl={subMenuAnchorEl}
            onClose={handleSubMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                onMouseLeave: handleSubMenuClose,
                sx: {
                  ml: 0.5,
                  minWidth: 180,
                  boxShadow: theme.shadows[8],
                },
              },
            }}
            disableRestoreFocus
          >
            <List dense>
              {filteredMenuItems
                .find(item => item.titleKey === activeSubMenu)
                ?.subItems?.map((subItem) => (
                  <ListItem key={subItem.path} disablePadding>
                    <ListItemButton
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        handleNavigation(subItem.path);
                        handleSubMenuClose();
                      }}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.title} />
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Popover>
        </>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 }, // Smaller padding on mobile
          width: isLoginPage ? '100%' : { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: isLoginPage ? 0 : '64px', // Height of AppBar
        }}
      >
        {children}
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleChangePasswordClick}>
          <ListItemIcon>
            <LockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t.nav.changePassword}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t.nav.logout}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </Box>
  );
};

export default AppLayout;